if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Media state and playback react immediately; only the thumbnail rail waits for the swipe to settle.
        this.debouncedSetActiveThumbnail = debounce(this.setActiveThumbnail.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        this.setActiveMediaState(activeMedia);
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.debouncedSetActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.setActiveMediaState(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMediaState(activeMedia) {
        if (!activeMedia || activeMedia.classList.contains('is-active')) return;

        this.elements.viewer.querySelectorAll('[data-media-id].is-active').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // A slide one position out can still be partly visible in the mobile carousel, so its video
      // keeps playing; two positions out is materialized but left paused. Distance is measured from
      // the active index, so forward and backward navigation behave identically.
      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        const activeIndex = slides.indexOf(activeItem);
        const toPlay = [];

        // loadContent() pauses all media, so everything in range is materialized before anything is
        // played - otherwise the last materialization would undo the plays that came before it.
        const activeMedia = activeItem.querySelector('.deferred-media');
        if (activeMedia) {
          this.materializeMedia(activeMedia);
          toPlay.push(activeMedia);
        }

        if (activeIndex !== -1) {
          slides.forEach((slide, index) => {
            const distance = Math.abs(index - activeIndex);
            if (distance === 0 || distance > 2) return;

            const deferredMedia = slide.querySelector('.deferred-media');
            if (!deferredMedia || !this.isShopifyVideo(deferredMedia)) return;

            this.materializeMedia(deferredMedia);
            if (distance === 1) toPlay.push(deferredMedia);
          });
        }

        toPlay.forEach((deferredMedia) => this.playMaterializedVideo(deferredMedia));
      }

      // Shopify-hosted video only: external video templates hold an iframe and models a model-viewer.
      isShopifyVideo(deferredMedia) {
        return !!deferredMedia.querySelector('template')?.content?.querySelector('video');
      }

      materializeMedia(deferredMedia) {
        if (deferredMedia.hasAttribute('loaded')) return;

        this.prepareShopifyVideo(deferredMedia);
        deferredMedia.loadContent(false);
      }

      playMaterializedVideo(deferredMedia) {
        const video = deferredMedia.querySelector('video');
        if (!video) return;

        this.revealOnFirstFrame(deferredMedia, video);
        video.play().catch(() => this.offerManualPlay(deferredMedia, video));
      }

      // Edits the inert template before DeferredMedia clones it, so the video is materialized and
      // starts buffering without loadContent's autoplay branch firing. Shopify-hosted video only:
      // external video templates hold an iframe and models a model-viewer, which are left untouched.
      prepareShopifyVideo(deferredMedia) {
        const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
        if (!templateVideo) return false;

        templateVideo.removeAttribute('autoplay');
        templateVideo.setAttribute('preload', 'auto');
        return true;
      }

      // Keeps the poster covering the video until a frame has actually been presented, so the handoff
      // never exposes an empty media area. If playback is refused, no frame arrives and the poster stays.
      // The flag lands on the media container and is never cleared: once a real frame has been shown,
      // pausing freezes it rather than reverting to the poster or the static preview.
      revealOnFirstFrame(deferredMedia, video) {
        if (deferredMedia.dataset.frameHandoff) return;
        deferredMedia.dataset.frameHandoff = 'true';

        const reveal = () => deferredMedia.closest('.product-media-container')?.classList.add('video-ready');
        if ('requestVideoFrameCallback' in video) {
          video.requestVideoFrameCallback(reveal);
        } else {
          video.addEventListener('playing', () => requestAnimationFrame(reveal), { once: true });
        }
      }

      // Autoplay refused (e.g. iOS Low Power Mode): leave the poster up and restore a tappable control
      // rather than retrying, since DeferredMedia's own poster handler no-ops once loaded is set.
      offerManualPlay(deferredMedia, video) {
        deferredMedia.classList.add('autoplay-blocked');
        const poster = deferredMedia.querySelector('.deferred-media__poster');
        if (!poster || poster.dataset.manualPlay) return;

        poster.dataset.manualPlay = 'true';
        poster.addEventListener('click', () => video.play().catch(() => {}));
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
